import { useState, useEffect, useCallback } from 'react';
import { registerSessionsService, mergeRegisterSessionsById } from '../lib/database';
import { isRegisterSessionActive } from '../lib/registerSessionUtils';
import { getDataSource } from '../lib/config';
import type { RegisterSession } from '../types';

interface UseRegisterSessionsReturn {
  sessions: RegisterSession[];
  activeSessions: RegisterSession[];
  loading: boolean;
  error: string | null;
  create: (session: Omit<RegisterSession, 'id'>) => Promise<RegisterSession>;
  update: (id: string, updates: Partial<RegisterSession>) => Promise<RegisterSession>;
  remove: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export const useRegisterSessions = (enabled = true): UseRegisterSessionsReturn => {
  const [sessions, setSessions] = useState<RegisterSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSessions = useCallback(async () => {
    const dataSource = getDataSource();

    if (dataSource === 'mock') {
      // For mock data, return empty array (sessions will be managed locally)
      setSessions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await registerSessionsService.getAll();
      setSessions(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load register sessions';
      setError(errorMessage);
      console.error('Error fetching register sessions:', err);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Real-time subscription so every consumer sees the live snapshot.
  //
  // Previously this hook ran a one-shot `getAll()` on mount, which meant `registerSessions` /
  // `activeSessions` would go stale whenever a session was opened or closed elsewhere — including
  // the now-shared register session opened on another device. Symptoms included Reports showing
  // "no active register" while POS happily let staff in (POS uses a separate `subscribeActive`
  // listener in App.tsx).
  //
  // The subscribe handler returns every register_session document; for shops with very long
  // histories this could become expensive, but it matches the previous getAll behavior exactly
  // (same query, just live). If volume becomes a concern we can switch to a bounded
  // subscribeByDateRange + activeSessions union later.
  useEffect(() => {
    if (!enabled) return;

    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      // Mock mode: keep local-only behaviour, no Firestore subscription.
      setSessions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    let receivedFirstSnapshot = false;

    // Seed from server so Reports has active sessions even if listeners race on empty cache.
    void registerSessionsService.getLiveSnapshot().then((data) => {
      setSessions((prev) => mergeRegisterSessionsById([prev, data]));
      setLoading(false);
      if (!receivedFirstSnapshot) {
        receivedFirstSnapshot = true;
        clearTimeout(safetyTimer);
      }
    }).catch((err) => {
      console.error('Error seeding register sessions:', err);
    });

    const safetyTimer = setTimeout(async () => {
      if (receivedFirstSnapshot) return;
      try {
        const data = await registerSessionsService.getLiveSnapshot();
        setSessions((prev) => mergeRegisterSessionsById([prev, data]));
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load register sessions';
        setError(errorMessage);
        console.error('Error fetching register sessions (fallback after subscribe timeout):', err);
      } finally {
        setLoading(false);
      }
    }, 5000);

    let unsubscribe: () => void;
    try {
      unsubscribe = registerSessionsService.subscribe(
        (data) => {
          setSessions((prev) => {
            if (data.length === 0 && prev.some((s) => s.isActive === true)) {
              return prev;
            }
            return mergeRegisterSessionsById([prev, data]);
          });
          if (!receivedFirstSnapshot) {
            receivedFirstSnapshot = true;
            setLoading(false);
            clearTimeout(safetyTimer);
          }
        },
        (err) => {
          // Live subscription error - surface it but don't keep `loading` stuck.
          setError(err.message || 'Register sessions subscription failed');
          if (!receivedFirstSnapshot) {
            receivedFirstSnapshot = true;
            setLoading(false);
            clearTimeout(safetyTimer);
          }
        }
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to subscribe to register sessions';
      setError(errorMessage);
      console.error('Error subscribing to register sessions:', err);
      setLoading(false);
      clearTimeout(safetyTimer);
      unsubscribe = () => {};
    }

    return () => {
      clearTimeout(safetyTimer);
      unsubscribe();
    };
  }, [enabled]);

  const create = useCallback(async (session: Omit<RegisterSession, 'id'>): Promise<RegisterSession> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      const newSession: RegisterSession = {
        ...session,
        id: `REG-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      };
      setSessions(prev => [...prev, newSession]);
      return newSession;
    }

    setError(null);
    try {
      const newSession = await registerSessionsService.create(session);
      // Optimistic insert; the live subscribe listener will reconcile the same row shortly.
      // De-dupe by id so the list doesn't briefly contain the same session twice.
      setSessions(prev => (prev.some(s => s.id === newSession.id) ? prev : [...prev, newSession]));
      return newSession;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create register session';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const update = useCallback(async (id: string, updates: Partial<RegisterSession>): Promise<RegisterSession> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      const updatedSession = sessions.find(s => s.id === id);
      if (!updatedSession) throw new Error('Session not found');
      const newSession = { ...updatedSession, ...updates };
      setSessions(prev => prev.map(s => s.id === id ? newSession : s));
      return newSession;
    }

    setError(null);
    try {
      const updatedSession = await registerSessionsService.update(id, updates);
      // Optimistic merge; the live subscribe listener will overwrite with the canonical row.
      setSessions(prev => prev.map(s => (s.id === id ? updatedSession : s)));
      return updatedSession;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update register session';
      setError(errorMessage);
      throw err;
    }
  }, [sessions]);

  const remove = useCallback(async (id: string): Promise<void> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      setSessions(prev => prev.filter(s => s.id !== id));
      return;
    }

    setError(null);
    try {
      await registerSessionsService.delete(id);
      setSessions(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete register session';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const refresh = useCallback(async () => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      await fetchSessions();
      return;
    }
    try {
      const data = await registerSessionsService.getLiveSnapshot();
      setSessions((prev) => mergeRegisterSessionsById([prev, data]));
    } catch (err) {
      console.error('Error refreshing register sessions:', err);
    }
  }, [fetchSessions]);

  const activeSessions = sessions.filter((s) => isRegisterSessionActive(s));

  return {
    sessions,
    activeSessions,
    loading,
    error,
    create,
    update,
    remove,
    refresh,
  };
};

